import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import {
  Check,
  Copy,
  ExternalLink,
  Eye,
  KeyRound,
  Link2,
  Loader2,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
} from 'lucide-react'

import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { apiClient } from '@/lib/api/client'
import type {
  ConsignmentDocument,
  CreateTrackingShareRequest,
  TrackingShare,
  TrackingShareScope,
} from '@/lib/api/types'
import { buildPublicTrackingShareUrl, getTrackingShareState } from '@/src/lib/public-tracking'

/**
 * Defaults for a link being created now. Documents stay off because they are
 * the one section that can carry commercial terms; containers and position are
 * the shipment's own journey, which is what the recipient asked to see.
 */
const defaultScope: TrackingShareScope = {
  summary: true,
  timeline: true,
  documents: false,
  live_position: true,
  containers: true,
}

function defaultExpiry() {
  const value = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
  value.setMinutes(value.getMinutes() - value.getTimezoneOffset())
  return value.toISOString().slice(0, 16)
}

function formatDate(value: string | null) {
  if (!value) return 'Never'
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value))
}

function documentLabel(document: ConsignmentDocument) {
  return document.file_name || document.type.replaceAll('_', ' ').toLowerCase()
}

function ShareStateBadge({ share }: { share: TrackingShare }) {
  const state = getTrackingShareState(share)
  return (
    <Badge
      variant={state === 'active' ? 'secondary' : state === 'revoked' ? 'destructive' : 'outline'}
    >
      {state === 'active' ? 'Active' : state === 'revoked' ? 'Revoked' : 'Expired'}
    </Badge>
  )
}

export function PublicTrackingShareManager({
  consignmentId,
  documents,
}: {
  consignmentId: string
  documents: ConsignmentDocument[]
}) {
  const [shares, setShares] = useState<TrackingShare[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [revealedUrl, setRevealedUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [pendingRevoke, setPendingRevoke] = useState<TrackingShare | null>(null)
  const [expiresAt, setExpiresAt] = useState(defaultExpiry)
  const [maxViews, setMaxViews] = useState(25)
  const [recipient, setRecipient] = useState('')
  const [passcode, setPasscode] = useState('')
  const [scope, setScope] = useState(defaultScope)
  const [documentIds, setDocumentIds] = useState<string[]>([])

  const availableDocuments = useMemo(
    () => documents.filter((document) => (document.status ?? 'ACTIVE') === 'ACTIVE'),
    [documents],
  )

  const loadShares = useCallback(async () => {
    setError(null)
    try {
      setShares(await apiClient.listTrackingShares(consignmentId))
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to load tracking links.')
    } finally {
      setLoading(false)
    }
  }, [consignmentId])

  useEffect(() => {
    void loadShares()
  }, [loadShares])

  function resetForm() {
    setExpiresAt(defaultExpiry())
    setMaxViews(25)
    setRecipient('')
    setPasscode('')
    setScope(defaultScope)
    setDocumentIds([])
  }

  async function createShare(event: FormEvent) {
    event.preventDefault()
    setError(null)
    if (passcode && passcode.trim().length < 6) {
      setError('Passcodes must contain at least 6 characters.')
      return
    }
    if (scope.documents && documentIds.length === 0) {
      setError('Choose at least one approved document or turn off document access.')
      return
    }

    const payload: CreateTrackingShareRequest = {
      expires_at: new Date(expiresAt).toISOString(),
      max_views: maxViews,
      scope: { ...scope, summary: true },
      document_ids: scope.documents ? documentIds : [],
      ...(recipient.trim() ? { recipient: recipient.trim() } : {}),
      ...(passcode.trim() ? { passcode: passcode.trim() } : {}),
    }

    setBusy(true)
    try {
      const share = await apiClient.createTrackingShare(consignmentId, payload)
      setRevealedUrl(buildPublicTrackingShareUrl(share.share_url, share))
      setCreateOpen(false)
      resetForm()
      await loadShares()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to create the tracking link.')
    } finally {
      setBusy(false)
    }
  }

  async function rotateShare(share: TrackingShare) {
    setBusy(true)
    setError(null)
    try {
      const rotated = await apiClient.rotateTrackingShare(consignmentId, share.id)
      setRevealedUrl(buildPublicTrackingShareUrl(rotated.share_url, rotated))
      await loadShares()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to rotate the tracking link.')
    } finally {
      setBusy(false)
    }
  }

  async function revokeShare() {
    if (!pendingRevoke) return
    setBusy(true)
    setError(null)
    try {
      await apiClient.revokeTrackingShare(consignmentId, pendingRevoke.id)
      setPendingRevoke(null)
      await loadShares()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to revoke the tracking link.')
    } finally {
      setBusy(false)
    }
  }

  async function copyRevealedUrl() {
    if (!revealedUrl) return
    try {
      await navigator.clipboard.writeText(revealedUrl)
      setCopied(true)
    } catch {
      setError('Unable to copy the tracking link. Select the link and copy it manually.')
    }
  }

  return (
    <section className="rounded-lg border bg-card" aria-labelledby="tracking-share-title">
      <div className="flex flex-col gap-3 border-b p-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <ShieldCheck className="size-4" aria-hidden="true" />
            <h2 id="tracking-share-title" className="font-semibold">
              Public tracking links
            </h2>
          </div>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Share a restricted tracking view. Direct links unlock once from a secret in the URL;
            passcode links require the recipient to enter the passcode as well.
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="size-4" aria-hidden="true" />
              Create link
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
            <form onSubmit={createShare}>
              <DialogHeader>
                <DialogTitle>Create public tracking link</DialogTitle>
                <DialogDescription>
                  Limit its lifetime, views, recipient, and visible tracking sections.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-5 py-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="share-expiry">Expires</Label>
                    <Input
                      id="share-expiry"
                      type="datetime-local"
                      required
                      value={expiresAt}
                      onChange={(event) => setExpiresAt(event.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="share-max-views">Maximum views</Label>
                    <Input
                      id="share-max-views"
                      type="number"
                      min={1}
                      max={10000}
                      required
                      value={maxViews}
                      onChange={(event) => setMaxViews(Number(event.target.value))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="share-recipient">Recipient email (optional)</Label>
                    <Input
                      id="share-recipient"
                      type="email"
                      placeholder="customer@example.com"
                      value={recipient}
                      onChange={(event) => setRecipient(event.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="share-passcode">Passcode (optional)</Label>
                    <Input
                      id="share-passcode"
                      type="password"
                      minLength={6}
                      autoComplete="new-password"
                      placeholder="At least 6 characters"
                      value={passcode}
                      onChange={(event) => setPasscode(event.target.value)}
                    />
                  </div>
                </div>

                <Separator />
                <fieldset className="space-y-3">
                  <legend className="text-sm font-medium">Visible sections</legend>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {(
                      [
                        ['summary', 'Shipment summary'],
                        ['timeline', 'Tracking timeline'],
                        ['live_position', 'Live vessel position'],
                        ['containers', 'Containers and movements'],
                        ['documents', 'Approved documents'],
                      ] as const
                    ).map(([key, label]) => (
                      <label key={key} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={scope[key]}
                          disabled={key === 'summary'}
                          onCheckedChange={(checked) =>
                            setScope((current) => ({ ...current, [key]: checked === true }))
                          }
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    The shipment summary is always included so recipients can identify the shared
                    consignment.
                  </p>
                </fieldset>

                {scope.documents && (
                  <fieldset className="space-y-3">
                    <legend className="text-sm font-medium">Approved documents</legend>
                    {availableDocuments.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No active consignment documents are available.
                      </p>
                    ) : (
                      <div className="max-h-44 space-y-2 overflow-y-auto rounded-lg border p-3">
                        {availableDocuments.map((document) => (
                          <label key={document.id} className="flex items-start gap-2 text-sm">
                            <Checkbox
                              checked={documentIds.includes(document.id)}
                              onCheckedChange={(checked) =>
                                setDocumentIds((current) =>
                                  checked === true
                                    ? [...new Set([...current, document.id])]
                                    : current.filter((id) => id !== document.id),
                                )
                              }
                            />
                            <span>
                              <span className="block font-medium capitalize">
                                {documentLabel(document)}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {document.type.replaceAll('_', ' ')}
                              </span>
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </fieldset>
                )}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={busy}>
                  {busy ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Link2 className="size-4" />
                  )}
                  Create link
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {error && (
        <Alert variant="destructive" className="m-4 w-auto">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading tracking links
        </div>
      ) : shares.length === 0 ? (
        <p className="p-4 text-sm text-muted-foreground">No public tracking links created yet.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Status</TableHead>
              <TableHead>Recipient</TableHead>
              <TableHead>Access</TableHead>
              <TableHead>Usage</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Last access</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shares.map((share) => {
              const state = getTrackingShareState(share)
              return (
                <TableRow key={share.id}>
                  <TableCell>
                    <ShareStateBadge share={share} />
                  </TableCell>
                  <TableCell>{share.recipient || 'Anyone with link'}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5">
                      {share.requires_passcode ? (
                        <KeyRound className="size-3.5" aria-hidden="true" />
                      ) : (
                        <Link2 className="size-3.5" aria-hidden="true" />
                      )}
                      {share.requires_passcode ? 'Link + passcode' : 'Direct link'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5">
                      <Eye className="size-3.5" aria-hidden="true" />
                      {share.view_count} / {share.max_views}
                    </span>
                  </TableCell>
                  <TableCell>{formatDate(share.expires_at)}</TableCell>
                  <TableCell>{formatDate(share.last_accessed_at)}</TableCell>
                  <TableCell>
                    <TooltipProvider>
                      <div className="flex justify-end gap-1">
                        {state === 'active' && (
                          <>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="icon-xs"
                                  variant="ghost"
                                  aria-label="Rotate public tracking link"
                                  disabled={busy}
                                  onClick={() => void rotateShare(share)}
                                >
                                  <RefreshCw className="size-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top">Rotate link</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="icon-xs"
                                  variant="ghost"
                                  aria-label="Revoke public tracking link"
                                  disabled={busy}
                                  onClick={() => setPendingRevoke(share)}
                                >
                                  <Trash2 className="size-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top">Revoke link</TooltipContent>
                            </Tooltip>
                          </>
                        )}
                      </div>
                    </TooltipProvider>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}

      <p className="border-t p-4 text-xs text-muted-foreground">
        For security, Helm never displays a link secret again. Rotate an active link to invalidate
        the old one and issue a new one-time link.
      </p>

      <Dialog
        open={Boolean(revealedUrl)}
        onOpenChange={(open) => {
          if (!open) {
            setRevealedUrl(null)
            setCopied(false)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tracking link ready</DialogTitle>
            <DialogDescription>
              Copy this link now. Helm will not display its secret after this dialog closes.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input aria-label="Public tracking link" value={revealedUrl ?? ''} readOnly />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    aria-label="Copy tracking link"
                    onClick={() => void copyRevealedUrl()}
                  >
                    {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {copied ? 'Link copied' : 'Copy tracking link'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <DialogFooter>
            {revealedUrl && (
              <Button asChild variant="outline">
                <a href={revealedUrl} target="_blank" rel="noreferrer">
                  <ExternalLink className="size-4" /> Preview
                </a>
              </Button>
            )}
            <Button
              type="button"
              onClick={() => {
                setRevealedUrl(null)
                setCopied(false)
              }}
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={Boolean(pendingRevoke)}
        onOpenChange={(open) => !open && setPendingRevoke(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke tracking link?</AlertDialogTitle>
            <AlertDialogDescription>
              Existing public sessions and the shared link will stop working. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={() => void revokeShare()}>
              Revoke link
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}
